import './tiptapSafeEditorPatch';
import { AvatarDropdown, AvatarName, ContextSwitcher, SelectLang, ThemeSwitch } from '@/components';
import { LocaleBridge } from '@/components/LocaleBridge';
import { ForbiddenRouteRedirect } from '@/modules/common/permissions/ForbiddenRouteRedirect';
import { filterMenuByPermissions } from '@/modules/common/permissions/menuFilter';
import { resolveEffectivePermissions } from '@/modules/common/permissions/permissionPreview';
import { resolveLayout } from '@/modules/common/permissions/permissionRegistry';
import { getCurrentUserInfo } from '@/modules/infrastructure/users/services/auth.api';
import { CurrentUser } from '@/modules/infrastructure/users/types/user.types';
import PositionGate from '@/modules/subject/userContext/components/PositionGate';
import { LinkOutlined } from '@ant-design/icons';
import type { Settings as LayoutSettings } from '@ant-design/pro-components';
import { SettingDrawer } from '@ant-design/pro-components';
import type { RunTimeLayoutConfig } from '@umijs/max';
import { getIntl, history, Link } from '@umijs/max';
import React from 'react';
import defaultSettings from '../config/defaultSettings';
import { errorConfig } from './requestErrorConfig';
import { requestConfig } from './requestConfig';
import { resolveApiBaseUrl } from './apiBase';
import { registerLoginBgSw } from './utils/registerLoginBgSw';
import { applyDocumentColorScheme, getStoredThemeMode, navThemeFor } from './utils/themeMode';
import '@ant-design/v5-patch-for-react-19';

const isDev = process.env.NODE_ENV === 'development';
const loginPath = '/user/login';

registerLoginBgSw();
applyDocumentColorScheme(getStoredThemeMode());

/**
 * @see https://umijs.org/docs/api/runtime-config#getinitialstate
 * */
export async function getInitialState(): Promise<{
  settings?: Partial<LayoutSettings>;
  currentUser?: CurrentUser;
  loading?: boolean;
  fetchUserInfo?: () => Promise<CurrentUser | undefined>;
}> {
  const settings = {
    ...defaultSettings,
    navTheme: navThemeFor(getStoredThemeMode()),
  } as Partial<LayoutSettings>;

  const fetchUserInfo = async () => {
    try {
      const userInfo = await getCurrentUserInfo();
      return userInfo || undefined;
    } catch (error) {
      history.push(loginPath);
    }
    return undefined;
  };
  // If it's not the login page, execute
  const { location } = history;
  if (location.pathname !== loginPath) {
    const currentUser = await fetchUserInfo();
    return {
      fetchUserInfo,
      currentUser,
      settings,
    };
  }
  return {
    fetchUserInfo,
    settings,
  };
}

// ProLayout supported APIs https://procomponents.ant.design/components/layout
export const layout: RunTimeLayoutConfig = ({ initialState, setInitialState }) => {
  const { permissions } = resolveEffectivePermissions(initialState?.currentUser?.permissions);
  const accessLayout = resolveLayout(permissions);

  return {
    menuDataRender: (menuData) => filterMenuByPermissions(menuData, accessLayout),
    title: getIntl().formatMessage({
      id: 'layout.header.workspace',
      defaultMessage: 'Рабочий кабинет методиста',
    }),
    logo: '/logo-ai-dovut.png',
    headerTitleRender: (logo, title) => (
      <a
        style={{
          display: 'inline-flex',
          alignItems: 'center',
          columnGap: 20,
        }}
      >
        {logo}
        {title}
      </a>
    ),
    actionsRender: () => [
      <ContextSwitcher key="ContextSwitcher" />,
      <ThemeSwitch key="ThemeSwitch" />,
      <SelectLang key="SelectLang" />,
    ],
    avatarProps: {
      src: initialState?.currentUser?.avatar || '/user-photo.png',
      title: <AvatarName />,
      render: (_, avatarChildren) => {
        return <AvatarDropdown>{avatarChildren}</AvatarDropdown>;
      },
    },
    onPageChange: () => {
      const { location } = history;
      // If not logged in, redirect to login
      if (!initialState?.currentUser && location.pathname !== loginPath) {
        history.push(loginPath);
      }
    },
    bgLayoutImgList: [
      {
        src: 'https://mdn.alipayobjects.com/yuyan_qk0oxh/afts/img/D2LWSqNny4sAAAAAAAAAAAAAFl94AQBr',
        left: 85,
        bottom: 100,
        height: '303px',
      },
      {
        src: 'https://mdn.alipayobjects.com/yuyan_qk0oxh/afts/img/C2TWRpJpiC0AAAAAAAAAAAAAFl94AQBr',
        bottom: -68,
        right: -45,
        height: '303px',
      },
      {
        src: 'https://mdn.alipayobjects.com/yuyan_qk0oxh/afts/img/F6vSTbj8KpYAAAAAAAAAAAAAFl94AQBr',
        bottom: 0,
        left: 0,
        width: '331px',
      },
    ],
    links: isDev
      ? [
          <Link key="openapi" to="/umi/plugin/openapi" target="_blank">
            <LinkOutlined />
            <span>OpenAPI Documentation</span>
          </Link>,
        ]
      : [],
    menuHeaderRender: undefined,
    // Custom 403 page
    // unAccessible: <div>unAccessible</div>,
    // Add a loading state
    childrenRender: (children) => {
      // if (initialState?.loading) return <PageLoading />;
      return (
        <>
          <LocaleBridge>{children}</LocaleBridge>
          <ForbiddenRouteRedirect layout={accessLayout} />
          <PositionGate />
          {isDev && (
            <SettingDrawer
              disableUrlParams
              enableDarkTheme
              settings={initialState?.settings}
              onSettingChange={(settings) => {
                setInitialState((preInitialState) => ({
                  ...preInitialState,
                  settings,
                }));
              }}
            />
          )}
        </>
      );
    },
    ...initialState?.settings,
  };
};

/**
 * @name request configuration, can configure error handling
 * It provides a unified network request and error handling solution based on axios and ahooks useRequest.
 * @doc https://umijs.org/docs/max/request#configuration
 */
const apiUrl = resolveApiBaseUrl();
console.log('App loaded. Target API URL:', apiUrl ?? '(same-origin /api)');

export const request = {
  // baseURL не задаём — каждый запрос собирается в interceptor как полный https URL
  timeout: requestConfig.timeout,
  ...requestConfig,
  // Keep errorThrower from errorConfig, use requestConfig's errorHandler (refresh token + delegate)
  errorConfig: {
    ...errorConfig.errorConfig,
    errorHandler: requestConfig.errorConfig?.errorHandler,
  },
};
